/**
 * ItemCollectionsSection — widget for managing collection membership on the Item Detail page.
 *
 * Shows tenant collections as a collapsible tree with switches.
 * On = item is in that collection. Tree mirrors the hierarchy from the Collections page.
 * Collapsed by default — only top-level collections visible until expanded.
 */

import { useState, useMemo, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Loader2, Layers3, ChevronDown, ChevronRight, Search } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  useStorefrontTenantKey,
  useCollections,
  useItemCollections,
  useAssignProducts,
  useRemoveProducts,
  collectionQueryKeys,
  type Collection,
} from "@/features/storefront/collections/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { SectionHeader } from "../../../SectionHeader";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";

/** Show search input only when collections exceed this count */
const SEARCH_THRESHOLD = 8;

// ── Tree utilities (same pattern as CollectionsPage) ────────

interface TreeNode extends Collection {
  children: TreeNode[];
}

function buildTree(collections: Collection[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  for (const c of collections) {
    map.set(c.name, { ...c, children: [] });
  }

  for (const c of collections) {
    const node = map.get(c.name)!;
    const parentName = c.parent_collection;
    if (parentName && map.has(parentName)) {
      map.get(parentName)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

/** Flatten a tree into a list, filtered by search query. Keeps parents visible if children match. */
function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  if (!query) return nodes;
  const q = query.toLowerCase();

  return nodes.reduce<TreeNode[]>((acc, node) => {
    const filteredChildren = filterTree(node.children, query);
    const selfMatches = node.title.toLowerCase().includes(q);

    if (selfMatches || filteredChildren.length > 0) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, []);
}

// ── Tree row component ──────────────────────────────────────

interface CollectionRowProps {
  node: TreeNode;
  depth: number;
  memberOf: Set<string>;
  pendingChanges: Set<string>;
  expanded: Set<string>;
  onToggleExpanded: (name: string) => void;
  onToggleMembership: (name: string, checked: boolean) => void;
}

function CollectionRow({
  node,
  depth,
  memberOf,
  pendingChanges,
  expanded,
  onToggleExpanded,
  onToggleMembership,
}: CollectionRowProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.name);
  const isMember = memberOf.has(node.name);
  const isPending = pendingChanges.has(node.name);
  const { rowPy, rowLabel } = useDetailDensity();

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-3 px-4 transition-opacity", rowPy,
          isPending && "opacity-60 pointer-events-none"
        )}
        style={{ paddingLeft: 16 + depth * 20 }}
      >
        {/* Expand/collapse chevron */}
        {hasChildren ? (
          <button
            type="button"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md hover:bg-muted transition-colors"
            onClick={() => onToggleExpanded(node.name)}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                isExpanded && "rotate-90"
              )}
            />
          </button>
        ) : (
          <div className="w-6 shrink-0" />
        )}

        {/* Title + hidden label */}
        <div className="flex-1 min-w-0">
          <span className={rowLabel} style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}>
            {node.title}
          </span>
          {!node.is_visible && (
            <span className="ml-2 text-sm text-muted-foreground">(hidden)</span>
          )}
        </div>

        {/* Product count */}
        <Badge
          variant="secondary"
          className="rounded-full text-xs font-normal shrink-0"
        >
          {node.product_count}
        </Badge>

        {/* Switch / spinner */}
        {isPending ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
        ) : (
          <Switch
            checked={isMember}
            onCheckedChange={(checked) => onToggleMembership(node.name, checked)}
            aria-label={`${isMember ? "Remove from" : "Add to"} ${node.title}`}
          />
        )}
      </div>

      {/* Children (only when expanded) */}
      {hasChildren && isExpanded && node.children.map((child) => (
        <CollectionRow
          key={child.name}
          node={child}
          depth={depth + 1}
          memberOf={memberOf}
          pendingChanges={pendingChanges}
          expanded={expanded}
          onToggleExpanded={onToggleExpanded}
          onToggleMembership={onToggleMembership}
        />
      ))}
    </>
  );
}

// ── Main component ──────────────────────────────────────────

export function ItemCollectionsSection({
  formData,
  embedded,
}: ShopifySectionWidgetProps & { embedded?: boolean }) {
  const itemCode = formData?.name as string | undefined;
  const storefrontEnabled =
    formData?.storefront_enabled === 1 || formData?.storefront_enabled === true;

  const { tenantKey, isLoading: tenantLoading } = useStorefrontTenantKey();
  const { data: allCollections, isLoading: collectionsLoading } =
    useCollections(tenantKey);
  const { data: itemCollections, isLoading: itemCollectionsLoading } =
    useItemCollections(tenantKey, itemCode ?? null);

  const queryClient = useQueryClient();
  const assignMutation = useAssignProducts(tenantKey);
  const removeMutation = useRemoveProducts(tenantKey);
  const pendingChanges = useMemo(() => new Set<string>(), []);
  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);

  const memberOf = useMemo(() => {
    if (!itemCollections) return new Set<string>();
    return new Set(itemCollections.map((c) => c.name));
  }, [itemCollections]);

  const memberCount = memberOf.size;
  const totalCount = allCollections?.length ?? 0;

  const tree = useMemo(() => {
    if (!allCollections) return [];
    const full = buildTree(allCollections);
    return debouncedSearch ? filterTree(full, debouncedSearch) : full;
  }, [allCollections, debouncedSearch]);

  const isLoading = tenantLoading || collectionsLoading || itemCollectionsLoading;

  const toggleSection = () => {
    if (isSectionOpen) setSearchQuery("");
    setIsSectionOpen((prev) => !prev);
  };

  const toggleNodeExpanded = useCallback((name: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const handleToggleMembership = useCallback(
    async (collectionName: string, checked: boolean) => {
      if (!itemCode || !tenantKey) return;

      // Optimistic update — flip the switch immediately
      const queryKey = collectionQueryKeys.itemCollections(itemCode);
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: Array<{ name: string }> | undefined) => {
        if (!old) return old;
        if (checked) {
          // Add — find the collection info from allCollections
          const info = allCollections?.find((c) => c.name === collectionName);
          return info ? [...old, info] : old;
        }
        // Remove
        return old.filter((c) => c.name !== collectionName);
      });

      try {
        if (checked) {
          await assignMutation.mutateAsync({
            collection_name: collectionName,
            item_codes: [itemCode],
          });
        } else {
          await removeMutation.mutateAsync({
            collection_name: collectionName,
            item_codes: [itemCode],
          });
        }
        // Refetch to confirm server state
        queryClient.invalidateQueries({ queryKey });
      } catch {
        // Rollback optimistic update
        queryClient.setQueryData(queryKey, previous);
        toast.error("Failed to update collection. Please try again.");
      }
    },
    [itemCode, tenantKey, allCollections, assignMutation, removeMutation, queryClient]
  );

  // When embedded, render card-less content for use inside another card
  if (embedded) {
    if (!storefrontEnabled) return null;
    return (
      <CollectionsInner
        isLoading={isLoading}
        allCollections={allCollections ?? null}
        tree={tree}
        memberCount={memberCount}
        totalCount={totalCount}
        memberOf={memberOf}
        pendingChanges={pendingChanges}
        expandedNodes={expandedNodes}
        isSectionOpen={isSectionOpen}
        searchQuery={searchQuery}
        debouncedSearch={debouncedSearch}
        onToggleSection={toggleSection}
        onToggleExpanded={toggleNodeExpanded}
        onToggleMembership={handleToggleMembership}
        onSearchChange={setSearchQuery}
      />
    );
  }

  if (!storefrontEnabled) {
    return (
      <div>
        <SectionHeader label="Collections" />
        <div className={cn(CARD, "px-4 py-5 opacity-50")}>
          <p className="text-sm text-muted-foreground">
            Enable &ldquo;Publish to Storefront&rdquo; above to manage collections.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Collections
      </h3>

      <div className={CARD}>
        <CollectionsInner
          isLoading={isLoading}
          allCollections={allCollections ?? null}
          tree={tree}
          memberCount={memberCount}
          totalCount={totalCount}
          memberOf={memberOf}
          pendingChanges={pendingChanges}
          expandedNodes={expandedNodes}
          isSectionOpen={isSectionOpen}
          searchQuery={searchQuery}
          debouncedSearch={debouncedSearch}
          onToggleSection={toggleSection}
          onToggleExpanded={toggleNodeExpanded}
          onToggleMembership={handleToggleMembership}
          onSearchChange={setSearchQuery}
        />
      </div>
    </div>
  );
}

// ── Inner content (reusable with or without card wrapper) ────

function CollectionsInner({
  isLoading,
  allCollections,
  tree,
  memberCount,
  totalCount,
  memberOf,
  pendingChanges,
  expandedNodes,
  isSectionOpen,
  searchQuery,
  debouncedSearch,
  onToggleSection,
  onToggleExpanded,
  onToggleMembership,
  onSearchChange,
}: {
  isLoading: boolean;
  allCollections: Collection[] | null;
  tree: TreeNode[];
  memberCount: number;
  totalCount: number;
  memberOf: Set<string>;
  pendingChanges: Set<string>;
  expandedNodes: Set<string>;
  isSectionOpen: boolean;
  searchQuery: string;
  debouncedSearch: string;
  onToggleSection: () => void;
  onToggleExpanded: (name: string) => void;
  onToggleMembership: (name: string, checked: boolean) => void;
  onSearchChange: (q: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { rowPx, rowPy, rowGap, dividerMx, rowLabel } = useDetailDensity();

  if (!allCollections || allCollections.length === 0) {
    return (
      <div className="flex flex-col items-center py-10">
        <Layers3 className="h-12 w-12 text-muted-foreground/40 mb-3" />
        <p className="text-base font-medium text-foreground">No collections yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Create one in the Collections page
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Collections row — matches KV row pattern */}
      <button
        type="button"
        className={cn(`w-full flex items-center ${rowGap} text-left`, rowPx, rowPy)}
        onClick={onToggleSection}
        aria-expanded={isSectionOpen}
      >
        <span className={cn(rowLabel, "w-1/2")} style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}>
          Collections
        </span>
        <div className={cn(rowLabel, "w-1/2 flex items-center justify-end gap-2")}>
          <span className="text-muted-foreground">
            {memberCount} of {totalCount}
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform shrink-0",
              isSectionOpen && "rotate-180"
            )}
          />
        </div>
      </button>

      {isSectionOpen && (
        <>
          {/* Search — only when many collections */}
          {totalCount > SEARCH_THRESHOLD && (
            <div className="px-4 pt-1 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search collections..."
                  aria-label="Search collections"
                  className="w-full pl-9 pr-3 py-2 text-sm rounded bg-muted/50 border-0 outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          )}

          {/* Tree list */}
          <div>
            {tree.map((node, i) => (
              <div key={node.name}>
                {i > 0 && <div className={cn(dividerMx, "border-t border-border/40")} />}
                <CollectionRow
                  node={node}
                  depth={0}
                  memberOf={memberOf}
                  pendingChanges={pendingChanges}
                  expanded={expandedNodes}
                  onToggleExpanded={onToggleExpanded}
                  onToggleMembership={onToggleMembership}
                />
              </div>
            ))}
            {tree.length === 0 && debouncedSearch && (
              <div className="px-4 py-4 text-center">
                <p className="text-sm text-muted-foreground">
                  No collections matching &ldquo;{debouncedSearch}&rdquo;
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
